<!-- src/pages/free-translation-requests.vue -->
<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-400 text-sm">
      <span class="font-semibold cursor-pointer" @click="$router.push('/')">Home</span>
      <span class="mx-2">/</span>
      <span class="text-gray-200 font-bold">Free Translation Requests</span>
    </nav>

    <h3 class="font-bold text-2xl text-white">Free Translation Requests</h3>

    <!-- Search & Filter -->
    <div class="flex items-center justify-between gap-4">
      <input
        v-model="search"
        @keyup.enter="fetchRequests"
        placeholder="Search by name, email, source/target language..."
        class="border border-gray-600 bg-gray-800 text-gray-100 rounded-md px-4 py-2 w-1/2"
      />

      <select
        v-model="filterStatus"
        class="border border-gray-600 bg-gray-800 text-gray-100 rounded-md px-3 py-2"
      >
        <option value="all">All statuses</option>
        <option value="new">New</option>
        <option value="in_progress">In progress</option>
        <option value="done">Done</option>
      </select>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse bg-gray-900 rounded-lg shadow">
        <thead>
          <tr class="bg-gray-800 text-left text-sm font-semibold text-gray-200">
            <th class="px-4 py-2">Name</th>
            <th class="px-4 py-2">Email</th>
            <th class="px-4 py-2">Languages</th>
            <th class="px-4 py-2">Notes</th>
            <th class="px-4 py-2">File</th>
            <th class="px-4 py-2">Status</th>
            <th class="px-4 py-2">Created At</th>
            <th class="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="req in filteredRequests"
            :key="req.id"
            class="border-t border-gray-800 hover:bg-gray-800 text-sm text-gray-100"
          >
            <td class="px-4 py-2">
              <div class="font-semibold">{{ req.name }}</div>
            </td>

            <td class="px-4 py-2">
              <a
                :href="`mailto:${req.email}`"
                class="text-blue-400 hover:underline"
              >
                {{ req.email }}
              </a>
            </td>

            <td class="px-4 py-2">
              <div>{{ req.source_language }} → {{ req.target_language }}</div>
            </td>

            <td class="px-4 py-2 max-w-md">
              <span class="block truncate">
                {{ req.notes || '-' }}
              </span>
            </td>

            <td class="px-4 py-2">
              <a
                v-if="req.file_url"
                :href="req.file_url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-400 hover:underline"
              >
                Download
              </a>
              <span v-else class="text-gray-500 text-xs italic">No file</span>
            </td>

            <td class="px-4 py-2">
              <span
                class="px-2 py-1 text-xs rounded-full capitalize"
                :class="statusBadgeClass(req.status)"
              >
                {{ req.status || 'new' }}
              </span>
            </td>

            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatDate(req.created_at) }}
            </td>

            <td class="px-4 py-2 text-center whitespace-nowrap">
              <button
                class="text-blue-400 hover:underline mx-1"
                @click="viewRequest(req)"
              >
                View
              </button>
              <button
                class="text-red-400 hover:underline mx-1"
                @click="deleteRequest(req.id)"
              >
                Delete
              </button>
            </td>
          </tr>

          <tr v-if="!loading && filteredRequests.length === 0">
            <td colspan="8" class="text-center py-4 text-gray-500">
              No requests found.
            </td>
          </tr>

          <tr v-if="loading">
            <td colspan="8" class="text-center py-4 text-gray-400">
              Loading...
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/apiClient";

const requests = ref([]);
const search = ref("");
const filterStatus = ref("all"); // all | new | in_progress | done
const loading = ref(false);

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

function statusBadgeClass(status) {
  switch (status) {
    case "new":
      return "bg-yellow-900 text-yellow-300";
    case "in_progress":
      return "bg-blue-900 text-blue-300";
    case "done":
      return "bg-green-900 text-green-300";
    default:
      return "bg-gray-800 text-gray-300";
  }
}

async function fetchRequests() {
  try {
    loading.value = true;
    const res = await apiClient.get("/free-translation-requests");
    const payload = res.data;

    if (payload && payload.data && Array.isArray(payload.data.requests)) {
      requests.value = payload.data.requests;
    } else if (Array.isArray(payload)) {
      requests.value = payload;
    } else {
      requests.value = [];
    }
  } catch (e) {
    console.error("Error fetching free translation requests:", e);
    requests.value = [];
  } finally {
    loading.value = false;
  }
}

// فلترة حسب الحالة + البحث
const filteredRequests = computed(() => {
  let list = requests.value;

  if (filterStatus.value !== "all") {
    list = list.filter((r) => (r.status || "new") === filterStatus.value);
  }

  if (!search.value.trim()) return list;

  const q = search.value.toLowerCase();
  return list.filter((r) => {
    const name = String(r.name || "").toLowerCase();
    const email = String(r.email || "").toLowerCase();
    const src = String(r.source_language || "").toLowerCase();
    const trg = String(r.target_language || "").toLowerCase();
    const notes = String(r.notes || "").toLowerCase();

    return (
      name.includes(q) ||
      email.includes(q) ||
      src.includes(q) ||
      trg.includes(q) ||
      notes.includes(q)
    );
  });
});

function viewRequest(r) {
  alert(
    `From: ${r.name} <${r.email}>\n` +
      `Languages: ${r.source_language} → ${r.target_language}\n` +
      `Status: ${r.status}\n\nNotes:\n${r.notes || "-"}`
  );
}

async function deleteRequest(id) {
  if (!confirm("Are you sure you want to delete this request?")) return;

  try {
    await apiClient.delete(`/free-translation-requests/${id}`);
    requests.value = requests.value.filter((r) => r.id !== id);
  } catch (e) {
    console.error(e);
    alert("Failed to delete request.");
  }
}

onMounted(() => {
  fetchRequests();
});
</script>

<style scoped>
table {
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
